// src/components/Auth/SignupModal.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Mail, Eye, EyeOff, UserPlus, ArrowLeft, Globe, Apple } from 'lucide-react';
import { FEATURES } from '@/config';

//src/components/Auth/SocialLoginButton.tsx
interface SignupModalProps {
  email?: string;
  sessionToken?: string;
  onSuccess: (hasCreatedAccount: boolean) => void;
  onBack?: () => void;
  onSignIn?: () => void; // New prop for navigation to login
}

export function SignupModal({ 
  email, 
  sessionToken,
  onSuccess,
  onBack,
  onSignIn
}: SignupModalProps) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoginPending, setSocialLoginPending] = useState<string | null>(null);
  const [error, setError] = useState('');
  
  // Password validation
  const validatePassword = (pass: string): { isValid: boolean; message: string } => {
    if (pass.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters' };
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(pass)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(pass)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }
    
    // Check for at least one number
    if (!/\d/.test(pass)) {
      return { isValid: false, message: 'Password must contain at least one number' };
    }
    
    // Check for at least one special character
    if (!/[^A-Za-z0-9]/.test(pass)) {
      return { isValid: false, message: 'Password must contain at least one special character' };
    }
    
    if (pass.length >= 12) {
      // For longer passwords, we can be more permissive about complexity
      return { isValid: true, message: '' };
    }

    // For shorter passwords (8-11 chars), ensure some complexity
    let complexityCount = 0;
    if (/[A-Z]/.test(pass)) complexityCount++;
    if (/[a-z]/.test(pass)) complexityCount++;
    if (/\d/.test(pass)) complexityCount++;
    if (/[^A-Za-z0-9]/.test(pass)) complexityCount++;
    
    if (complexityCount < 3) {
      return { 
        isValid: false, 
        message: 'Password must include at least 3 of the following: uppercase letters, lowercase letters, numbers, and special characters'
      };
    }
    return { isValid: true, message: '' };
  };
  
  // Handle password creation
  const handleCreateAccount_UNUSED = async (e: React.FormEvent) => {
    console.log('Creating account for:', email);
    e.preventDefault();
    setError('');
    
    /* // Validation checks remain the same
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    } */
    
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }
    
    /* if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    } */
    
    setIsLoading(true);
    
    try {
      // FIXED: Use the API_BASE_URL environment variable like in social login
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      
      // Call API to create the account with absolute URL
      const response = await fetch(`${API_BASE_URL}/api/auth/user-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          name, 
          email, 
          password, 
          sessionToken 
        }),
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create account');
      }
      
      // Store all required values in localStorage with exact variable names expected by AuthFlowController
      //localStorage.setItem('tripperUserName', name);
      localStorage.setItem('tripperUserEmail', email);
      localStorage.setItem('tripperHasAccount', 'true');
      localStorage.setItem('tripperCodeValidated', 'true');
      //localStorage.setItem('tripperAuthenticated', 'true');
      
      // Store user ID if returned
      if (result.userId) {
        localStorage.setItem('tripperUserId', result.userId);
      }
      // Set temporary flag in sessionStorage (not localStorage)
      sessionStorage.setItem('tripperAccountCreated', 'true');
      // Also set a localStorage backup in case session is cleared
      localStorage.setItem('tripperAccountJustCreated', 'true');      
  
      console.log('Account created, storage variables set:', {
        tripperUserName: name,
        tripperUserEmail: email,
        tripperHasAccount: true,
        tripperCodeValidated: true,
        tripperAccountCreated: true,
        sessionToken: sessionToken
      });
      
      console.log("Server response token:", result.token);
      console.log("All response data:", result);

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        console.log("Token saved:", localStorage.getItem('authToken'));
      }

      // Indicate successful account creation
      onSuccess(true);
  
    } catch (err) {
      console.error('Error creating account:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 1. Update the API call in handleCreateAccount to not require name
  const handleCreateAccount = async (e: React.FormEvent) => {
    console.log('Creating account for:', email);
    e.preventDefault();
    setError('');
    
    // Password validation only
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      
      // Check if we have a name stored in localStorage
      const storedName = localStorage.getItem('tripperFullName') || '';
      
      // If no stored name, create a name from the email
      let nameToUse = storedName;
      if (!nameToUse && email && typeof email === 'string') {
        // Extract username from email (part before @)
        const emailParts = email.split('@');
        if (emailParts.length > 0) {
          // Capitalize first letter and replace dots/underscores with spaces
          const username = emailParts[0]
            .replace(/[._]/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
          
          nameToUse = `${username}`;
        } else {
          // Fallback with unique identifier
          nameToUse = `Tripper User ${Math.floor(Math.random() * 10000)}`;
        }
      } else if (!nameToUse) {
        // Ultimate fallback with unique timestamp
        nameToUse = `Tripper User ${Date.now().toString().slice(-5)}`;
      }
      
      // Modified request body - use stored name or default
      const response = await fetch(`${API_BASE_URL}/api/auth/user-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          name: nameToUse, // Use stored name or default
          email, 
          password, 
          sessionToken 
        }),
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create account');
      }
      
      // Store values in localStorage
      localStorage.setItem('tripperUserEmail', email);
      localStorage.setItem('tripperHasAccount', 'true');
      localStorage.setItem('tripperCodeValidated', 'true');
      
      // Store user ID if returned
      if (result.userId) {
        localStorage.setItem('tripperUserId', result.userId);
      }
      
      // Set temporary flags
      sessionStorage.setItem('tripperAccountCreated', 'true');
      localStorage.setItem('tripperAccountJustCreated', 'true');
      
      console.log('Account created, storage variables set:', {
        tripperUserEmail: email,
        tripperHasAccount: true,
        tripperCodeValidated: true,
        tripperAccountCreated: true,
        sessionToken: sessionToken
      });
      
      console.log("Server response token:", result.token);
      console.log("All response data:", result);

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        console.log("Token saved:", localStorage.getItem('authToken'));
      }

      // Indicate successful account creation
      onSuccess(true);

    } catch (err) {
      console.error('Error creating account:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle social login
  const handleProviderLogin = async (provider: 'google' | 'microsoft' | 'apple') => {
    console.log('Logging in with provider:', provider);
    setError('');
    setSocialLoginPending(provider);
    
    try {
      // For OAuth providers, set storage flags
      sessionStorage.setItem('socialAuthProvider', provider);
      console.log(`Setting socialAuthProvider to ${provider}`);
      
      // Store email for after the redirect
      if (email) {
        sessionStorage.setItem('socialAuthEmail', email);
      }
      
      // Store session token if available
      if (sessionToken) {
        sessionStorage.setItem('tripperSessionToken', sessionToken);
      }
      
      // IMPORTANT: Add this line to preserve code validation state
      localStorage.setItem('tripperCodeValidated', 'true');
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const authUrl = `${API_BASE_URL}/api/auth/${provider}`;
      
      console.log('Redirecting to:', authUrl);
      
      // Use a more direct approach for redirection
      window.location.href = authUrl;
      
      return;
    } catch (err) {
      console.error('Provider login error:', err);
      setError(`Failed to login with ${provider}`);
      setSocialLoginPending(null);
    }
  };
    // Skip account creation and go to login
  const handleSkipToLogin = () => {
    onSuccess(false);
  };
  
  return (
    <>
      {/* Header */}
      {/* <div className="px-6 py-4 border-b border-gray-200" style={{ height: '90px' }}>
        <h2 className="text-lg font-semibold text-gray-900">Sign Up</h2> */}
        {/* <p className="text-sm text-gray-500 mt-1">
          Set up your Tripper account
        </p> */}
     {/*  </div> */}

     {/* Header */}
      <div className="px-6 flex flex-col justify-center border-b border-gray-200" style={{ height: '90px' }}>
         <h2 className="text-gray-900" style={{
          fontSize: 'var(--auth-font-size-2xl)',
          fontWeight: 'var(--auth-font-weight-regular)',
          fontFamily: 'var(--auth-header-font-family)'
        }}> Sign Up</h2>
      </div>

      <div className="p-6 space-y-6">
      {/* Social Login Buttons - with better spacing and Apple removed */}
      {(true) && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault(); // Prevent any default behavior
              e.stopPropagation(); // Stop event propagation
              handleProviderLogin('google');
            }}
            disabled={isLoading || socialLoginPending !== null}
            className="w-full border border-gray-300 rounded-md flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors relative"
            style={{
              height: 'var(--auth-button-height)',
              padding: 'var(--auth-button-padding)',
              fontSize: 'var(--auth-button-font-size)',
              fontWeight: 'var(--auth-button-font-weight)',
              borderRadius: 'var(--auth-button-border-radius)'
            }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-gray-700 font-medium">Continue with Google</span>
            {socialLoginPending === 'google' && (
              <span className="absolute right-3 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
            )}
          </button>

          <button
            type="button"
            onClick={() => handleProviderLogin('microsoft')}
            disabled={isLoading || socialLoginPending !== null}
            className="w-full border border-gray-300 rounded-md flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors relative"
            style={{
              height: 'var(--auth-button-height)',
              padding: 'var(--auth-button-padding)',
              fontSize: 'var(--auth-button-font-size)',
              fontWeight: 'var(--auth-button-font-weight)',
              borderRadius: 'var(--auth-button-border-radius)'
            }}
          >
            <Globe size={20} className="text-blue-500" />
            <span className="text-gray-700 font-medium">Continue with Microsoft</span>
            {socialLoginPending === 'microsoft' && (
              <span className="absolute right-3 h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
            )}
          </button>
        </div>
      )}

      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">Or</span>
        </div>
      </div>

      <form onSubmit={handleCreateAccount} className="space-y-6"> {/* Increased from space-y-5 */}
      {/* Email (pre-filled and disabled) */}
      <div>
        <label 
          htmlFor="email" 
          className="block text-gray-700 mb-3"
          style={{
            fontSize: 'var(--auth-label-font-size)',
            fontWeight: 'var(--auth-label-font-weight)',
            fontFamily: 'var(--auth-font-family)'
          }}
        >
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email || ''}
          className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100"
          style={{
            borderRadius: 'var(--auth-button-border-radius)'
          }}
          disabled
        />
      </div>
      
      {/* Name Input */}
      {/* <div className="mt-6">  */}{/* Added explicit top margin */}
        {/* <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-3"> */} {/* Increased from mb-2 */}
        {/*   Full Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your full name"
          disabled={isLoading}
          autoComplete="name"
        />
      </div> */}
      
      {/* Password Input */}
      <div className="mt-6"> {/* Added explicit top margin */}
        <label 
          htmlFor="password" 
          className="block text-gray-700 mb-3"
          style={{
            fontSize: 'var(--auth-label-font-size)',
            fontWeight: 'var(--auth-label-font-weight)',
            fontFamily: 'var(--auth-font-family)'
          }}
        >
          Create Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            style={{
              borderRadius: 'var(--auth-button-border-radius)'
            }}
            placeholder="Create a strong password"
            disabled={isLoading}
            autoComplete="new-password"
            aria-label="Create a strong password"
            required
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />} {/* Increased from 18 */}
          </button>
        </div>
        <div className="text-gray-500" style={{
          fontSize: 'var(--auth-font-size-xs)',
          marginTop: 'var(--auth-compact-spacing)',
          lineHeight: 'var(--auth-line-height-base)'
        }}>{/* Increased from mt-2 */}
          Password must be at least 8 characters with uppercase, lowercase, number, and special character.
        </div>
      </div>

      {/* Confirm Password */}
      {/* <div className="mt-6">  */}{/* Added explicit top margin */}
        {/* <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-3"> */} {/* Increased from mb-2 */}
        {/*   Confirm Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            id="confirmPassword"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Confirm your password"
            disabled={isLoading}
            autoComplete="new-password"
            aria-label="Confirm your password"
            required
          />
        </div>
      </div> */}
      
      {/* Error Message */}
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 text-red-600 text-sm p-3 bg-red-50 border border-red-100 rounded-md mt-4" /* Added mt-4 */
          >
            <AlertCircle size={16} />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Submit Button - with better spacing */}
      <button
        type="submit"
        disabled={isLoading}
        className={`w-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2
          ${isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition-colors'}`}
        style={{
          height: 'var(--auth-button-height)',
          padding: 'var(--auth-button-padding)',
          fontSize: 'var(--auth-button-font-size)',
          fontWeight: 'var(--auth-button-font-weight)',
          borderRadius: 'var(--auth-button-border-radius)'
        }}
      >
        {isLoading ? (
          <div className="flex items-center">
            <span className="h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span> {/* Increased spinner size */}
            <span>Creating Account...</span>
          </div>
        ) : (
          <>
            <UserPlus size={20} /> {/* Increased from 18 */}
            <span>Create Account</span>
          </>
        )}
      </button>
    </form>
      
      {/* Login Link - with proper path */}
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={onSignIn}
          className="text-blue-600 hover:underline text-sm bg-transparent border-none p-0 m-0 inline font-normal"
        >
          Already have an account? Sign in instead
        </button>
      </div>
      
      {/* Back Button */}
      {onBack && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1 mx-auto"
          >
            <ArrowLeft size={16} />
            <span>Back to Invite Code</span>
          </button>
        </div>
      )}
    </div>
    </>
  );
}